import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Chip,
  Button
} from '@mui/material';
import { ArrowForward as ArrowForwardIcon } from '@mui/icons-material';
import { getIncompleteInvoices } from '../../../services/dashboardService';
import { IncompleteInvoice } from '../../../types/dashboard';
import { format } from 'date-fns';
import { ar } from 'date-fns/locale';

const IncompleteInvoicesWidget: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<IncompleteInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const result = await getIncompleteInvoices();
      setData(result);
      setError(null);
    } catch (err) {
      setError('فشل في تحميل الفواتير الغير مكتملة');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewInvoice = (invoice: IncompleteInvoice) => {
    // استخدام معرف صفحة الفاتورة (pageId) للانتقال إلى الرابط الصحيح
    if (!invoice.pageId) {
      console.error('معرف صفحة الفاتورة غير موجود:', invoice);
      return;
    }
    navigate(`/invoices/${invoice.pageId}/${invoice._id}/view`);
  };

  if (loading) {
    return (
      <Card sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
        <CircularProgress />
      </Card>
    );
  }

  if (error) {
    return (
      <Card sx={{ height: '100%', p: 3, minHeight: 300 }}>
        <Typography color="error">{error}</Typography>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        height: '100%',
        borderTop: '4px solid #e74c3c',
        borderRadius: 2,
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-5px)',
          boxShadow: '0 8px 30px rgba(0,0,0,0.15)'
        }
      }}
    >
      <CardContent sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, pb: 2, borderBottom: '2px solid #f0f0f0' }}>
          <Box
            sx={{
              width: 50,
              height: 50,
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 24,
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              color: 'white',
              mr: 2
            }}
          >
            ⚠️
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2c3e50', mb: 0.5 }}>
              الفواتير الغير مكتملة
            </Typography>
            <Typography variant="body2" sx={{ color: '#95a5a6' }}>
              <Chip 
                label={`${data.length} فاتورة`} 
                size="small" 
                sx={{ 
                  bgcolor: '#fee', 
                  color: '#e74c3c',
                  fontWeight: 'bold'
                }} 
              /> تحتاج إلى إكمال
            </Typography>
          </Box>
        </Box>

        {/* Invoice List */}
        {data.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h3" sx={{ opacity: 0.3, mb: 2 }}>✅</Typography>
            <Typography color="textSecondary">لا توجد فواتير غير مكتملة</Typography>
          </Box>
        ) : (
          <List sx={{ maxHeight: 300, overflow: 'auto' }}>
            {data.map((invoice) => (
              <ListItem
                key={invoice._id}
                sx={{
                  bgcolor: '#f8f9fa',
                  borderRadius: 2,
                  mb: 1,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: '#e9ecef',
                    transform: 'translateX(-5px)'
                  }
                }}
                onClick={() => handleViewInvoice(invoice)}
              >
                <ListItemText
                  primary={
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#2c3e50' }}>
                      فاتورة #{invoice.invoiceNumber}
                      {invoice.branchName && (
                        <Chip
                          label={invoice.branchName}
                          size="small"
                          sx={{
                            ml: 1,
                            height: 20,
                            fontSize: '0.7rem',
                            bgcolor: '#e3f2fd',
                            color: '#1976d2'
                          }}
                        />
                      )}
                    </Typography>
                  }
                  secondary={
                    <Typography variant="caption" sx={{ color: '#95a5a6' }}>
                      📅 {(() => {
                        const dateStr = invoice.date.split('T')[0];
                        return dateStr;
                      })()} | {invoice.customerName || 'غير محدد'}
                    </Typography>
                  }
                />
                <Box sx={{ textAlign: 'left' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#e74c3c', display: 'block' }}>
                    متبقي: {(invoice.remaining || 0).toLocaleString('ar-SA')} ر.س
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#95a5a6', display: 'block' }}>
                    الإجمالي: {invoice.total.toLocaleString('ar-SA')} ر.س
                  </Typography>
                </Box>
              </ListItem>
            ))}
          </List>
        )}

        {data.length > 5 && (
          <Button
            fullWidth
            variant="outlined"
            endIcon={<ArrowForwardIcon />}
            onClick={() => navigate('/invoices')}
            sx={{
              mt: 2,
              borderColor: '#e74c3c',
              color: '#e74c3c',
              fontWeight: 'bold',
              '&:hover': {
                borderColor: '#c0392b',
                bgcolor: 'rgba(231, 76, 60, 0.05)'
              }
            }}
          >
            عرض جميع الفواتير ({data.length})
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default IncompleteInvoicesWidget;